import { GuideLinkControl } from '../components/GuideLinkControl';
import { type GuideSurfaceId, HOLDOUT_SURFACES } from '../constants/featureLinks';
import { useFeatureLinksAccess, useGuideLinksGate } from '../hooks/useFeatureLinksAccess';
import { useGuideDrawer } from '../hooks/useGuideDrawer';
import { useGuideLinkTracking } from '../hooks/useGuideLinkTracking';
import { useGuideSubjectLanguage } from '../hooks/useGuideSubjectLanguage';
import { useGuideTopics } from '../store/api';
import type { GuideLinkShape } from '../types';
import { guideLinkWording } from '../utils/guideLinkWording';
import { resolveGuideLinkTarget } from '../utils/resolveGuideLinkTarget';
import { applySubjectLanguage } from '../utils/subjectLanguage';

type GuideLinkTargetProps = { slug: string; topic?: never } | { topic: string; slug?: never };

export type GuideLinkProps = GuideLinkTargetProps & {
  /** Where the link lives, for analytics; add new ids to GUIDE_SURFACES. */
  surfaceId: GuideSurfaceId;
  shape: GuideLinkShape;
  /** Used when the primary target has no published article. */
  fallbackSlug?: string;
  /** Locked-state secondary action only: the wording becomes "How {featureName} works". */
  featureName?: string;
};

/**
 * Opens the matching guide article in the drawer; renders nothing without the
 * gate, in the holdout's control arm, or without a published article.
 */
export function GuideLink({
  surfaceId,
  shape,
  slug,
  topic,
  fallbackSlug,
  featureName,
}: GuideLinkProps) {
  const gate = useGuideLinksGate();
  const index = useGuideTopics(undefined, { skip: !gate });
  const drawer = useGuideDrawer();
  const resolved = resolveGuideLinkTarget(index.data?.topics, { slug, topic, fallbackSlug });
  // Gate, then the article, then the holdout (GDN-3707): a Slice A or B link
  // logs its exposure once an article resolved, whether the arm renders or
  // suppresses it. Slice C surfaces follow the arm and log nothing.
  const { enabled } = useFeatureLinksAccess({
    logExposure: gate && resolved !== undefined && HOLDOUT_SURFACES.has(surfaceId),
  });
  const shown = enabled ? resolved : undefined;
  const tracking = useGuideLinkTracking({
    surfaceId,
    shape,
    article: shown ? { slug: shown.article.slug, topic: shown.topic.slug } : undefined,
  });
  const subjectLanguage = useGuideSubjectLanguage();
  if (!shown) return null;
  // Hubs describe a topic; direct tasks name the job the article covers.
  // Locked features keep their explicit wording unless the access fallback resolves.
  const readsArticleTitle =
    shape === 'secondary-action' &&
    (shown.viaFallback ||
      shown.article.kind === 'troubleshooting' ||
      (!featureName && shown.article.kind !== 'hub'));
  // Catalog titles say "patient"; the link says what this clinic says.
  const subject = applySubjectLanguage(
    shape === 'section-control' ? shown.article.title : shown.topic.title,
    subjectLanguage,
  );
  const wording = guideLinkWording(shape, {
    subject,
    featureName: shown.viaFallback ? undefined : featureName,
    articleTitle: readsArticleTitle
      ? applySubjectLanguage(shown.article.title, subjectLanguage)
      : undefined,
  });
  return (
    <GuideLinkControl
      shape={shape}
      {...wording}
      onReveal={tracking.hovered}
      onActivate={() => {
        tracking.clicked();
        drawer.open({ slug: shown.article.slug, surfaceId });
      }}
    />
  );
}
